import { IconButton } from '@ads/components-react';
import { PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import type { ColumnDef } from '@tanstack/react-table';
import type { ReactNode } from 'react';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { SerieVideoLangViewModel } from '@/shared/api/generated';
import { DataTable } from '@/shared/ui/organisms/DataTable';

export interface SerieVideoLangTableProps {
  readonly data: readonly SerieVideoLangViewModel[];
  readonly isLoading: boolean;
  readonly currentPage: number;
  readonly totalPages: number;
  readonly totalCount: number;
  readonly pageSize: number;
  readonly onPageChange: (page: number) => void;
  readonly emptyStateAction?: ReactNode;
  readonly onEdit: (lang: SerieVideoLangViewModel) => void;
  readonly onDelete: (lang: SerieVideoLangViewModel) => void;
}

/**
 * Tabela de Idiomas do Vídeo. Colunas Idioma, Título e Ações — editar/excluir
 * como ícones diretos, sem menu "...". Fiel ao frame *Idioma do Vídeo ·
 * Listagem* de `design/series.pen`.
 */
export function SerieVideoLangTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalCount,
  pageSize,
  onPageChange,
  emptyStateAction,
  onEdit,
  onDelete,
}: SerieVideoLangTableProps) {
  const { t } = useTranslation('series');

  const columns = useMemo<ColumnDef<SerieVideoLangViewModel, unknown>[]>(
    () => [
      {
        accessorKey: 'cultureInfo',
        header: t('videoLangs.table.columns.language'),
        cell: info => <span className="ts-paragraph-xxs">{(info.getValue() as string) ?? ''}</span>,
      },
      {
        accessorKey: 'title',
        header: t('videoLangs.table.columns.title'),
        cell: info => (
          <span className="ts-subtitle-xxs text-ctx-content-title">
            {(info.getValue() as string) ?? ''}
          </span>
        ),
      },
      {
        id: 'actions',
        header: () => <div className="text-right">{t('videoLangs.table.columns.actions')}</div>,
        enableSorting: false,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-quark">
            <IconButton
              icon={PencilSquareIcon}
              ariaLabel={t('videoLangs.table.edit')}
              hierarchy="tertiary"
              size="md"
              onClick={() => onEdit(row.original)}
            />
            <IconButton
              icon={TrashIcon}
              ariaLabel={t('videoLangs.table.delete')}
              hierarchy="tertiary"
              className="!text-base-danger-600"
              size="md"
              onClick={() => onDelete(row.original)}
            />
          </div>
        ),
      },
    ],
    [t, onEdit, onDelete],
  );

  return (
    <DataTable
      data={data as SerieVideoLangViewModel[]}
      columns={columns}
      isLoading={isLoading}
      currentPage={currentPage}
      totalPages={totalPages}
      totalCount={totalCount}
      pageSize={pageSize}
      onPageChange={onPageChange}
      emptyStateMessage={t('videoLangs.table.empty')}
      emptyStateDescription={t('videoLangs.table.emptyDescription')}
      {...(emptyStateAction ? { emptyStateAction } : {})}
    />
  );
}
